import { useApi } from "./api";

/**
 * Servicio para la gestión de operaciones en el sistema de documentación vehicular.
 */
export const useOperationService = () => {
  // Obtener instancia de cliente HTTP con configuración base (incluye prefijo '/vehicle')
  const api = useApi();

  return {
    /**
     * Obtiene todas las operaciones con soporte para paginación, búsqueda y ordenamiento
     */
    getAllOperations: async (params = {}) => {
      try {
        const response = await api.get("/vehicle/operations", { params });
        return response.data;
      } catch (error) {
        console.error("Error obteniendo operaciones:", error);
        throw error.response?.data || { message: error.message };
      }
    },

    getAvailableVehiclesForOperation: async (operationId, params = {}) => {
      try {
        // Enviamos todos los parámetros, incluyendo vehiclePage y combinedPage
        const queryParams = {
          limit: params.limit || 10,
          vehiclePage: params.vehiclePage || 1,
          combinedPage: params.combinedPage || 1,
          search: params.search || "",
          includeInactive: params.includeInactive || false,
          vehicleType: params.vehicleType || "all",
        };

        const response = await api.get(
          `/vehicle/operation/${operationId}/available-vehicles`,
          {
            params: queryParams,
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo vehículos disponibles:", error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Obtiene una operación específica por su ID
     */
    getOperationById: async (id) => {
      try {
        const response = await api.get(`/vehicle/operations/${id}`);
        return response.data;
      } catch (error) {
        console.error(`Error obteniendo operación con ID ${id}:`, error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Crea una nueva operación
     */
    createOperation: async (data) => {
      try {
        const response = await api.post("/vehicle/operations", data);
        return response.data;
      } catch (error) {
        console.error("Error creando operación:", error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Actualiza una operación existente
     */
    updateOperation: async (id, data) => {
      try {
        const response = await api.put(`/vehicle/operations/${id}`, data);
        return response.data;
      } catch (error) {
        console.error(`Error actualizando operación con ID ${id}:`, error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Elimina una operación
     */
    deleteOperation: async (id) => {
      try {
        const response = await api.delete(`/vehicle/operations/${id}`);
        return response.data;
      } catch (error) {
        console.error(`Error eliminando operación con ID ${id}:`, error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Obtiene estructura jerárquica de operaciones
     */
    getOperationsHierarchy: async (params = {}) => {
      try {
        const response = await api.get("/vehicle/operations-hierarchy", {
          params,
        });
        return response.data;
      } catch (error) {
        console.error("Error obteniendo jerarquía de operaciones:", error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Ejecuta migración de operaciones a nueva estructura (ya esta en deshuso)
     */
    migrateOperations: async () => {
      try {
        const response = await api.post("/vehicle/operations-migrate");
        return response.data;
      } catch (error) {
        console.error("Error en migración de operaciones:", error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Asignar vehículo a operación con ruta específica
     */
    assignVehicleToOperationWithRoute: async (data) => {
      try {
        const response = await api.post("/vehicle/vehicle-operation", {
          ...data,
          operation_route_id: data.operationRouteId,
        });
        return response.data;
      } catch (error) {
        console.error("Error asignando vehículo a operación con ruta:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Desactiva una relación vehículo-operación
     */
    deactivateVehicleOperationRelation: async (relationId, comment) => {
      try {
        const response = await api.put(
          `/vehicle/vehicle-operation/${relationId}/deactivate`,
          {
            comment,
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error desactivando relación vehículo-operación:", error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Obtiene relaciones de un vehículo con operaciones
     */
    getVehicleOperationRelations: async (vehicleId, onlyActive = false) => {
      try {
        const response = await api.get(
          `/vehicle/vehicle/${vehicleId}/operations`,
          {
            params: { onlyActive },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo relaciones vehículo-operación:", error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Obtener rutas asociadas a una operación
     */
    getOperationRoutes: async (operationId) => {
      try {
        const response = await api.get(
          `/vehicle/operation/${operationId}/routes`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error obteniendo rutas de operación ${operationId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Obtener ruta por defecto de una operación
     */
    getDefaultRoute: async (operationId) => {
      try {
        const response = await api.get(
          `/vehicle/operation/${operationId}/routes/default`
        );
        return response.data;
      } catch (error) {
        console.error(`Error obteniendo ruta por defecto:`, error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Asignar ruta a operación
     */
    assignRouteToOperation: async (operationId, routeData) => {
      try {
        const response = await api.post(
          `/vehicle/operation/${operationId}/routes`,
          routeData
        );
        return response.data;
      } catch (error) {
        console.error(`Error asignando ruta a operación:`, error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Actualizar relación operación-ruta
     */
    updateOperationRoute: async (relationId, updateData) => {
      try {
        const response = await api.put(
          `/vehicle/operation-route/${relationId}`,
          updateData
        );
        return response.data;
      } catch (error) {
        console.error(`Error actualizando relación operación-ruta:`, error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Eliminar ruta de operación
     */
    removeRouteFromOperation: async (relationId) => {
      try {
        const response = await api.delete(
          `/vehicle/operation-route/${relationId}`
        );
        return response.data;
      } catch (error) {
        console.error(`Error eliminando ruta de operación:`, error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Reordenar rutas de una operación
     */
    reorderOperationRoutes: async (operationId, routes) => {
      try {
        const response = await api.put(
          `/vehicle/operation/${operationId}/routes/reorder`,
          { routes }
        );
        return response.data;
      } catch (error) {
        console.error(`Error reordenando rutas:`, error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Obtener vehículos por operación y ruta
     */
    getVehiclesByOperationAndRoute: async (operationId, params = {}) => {
      try {
        const queryParams = {
          ...params,
          operationRouteId: params.operationRouteId || null,
        };

        const response = await api.get(
          `/vehicle/operation/${operationId}/vehicles`,
          { params: queryParams }
        );
        return response.data;
      } catch (error) {
        console.error(
          "Error obteniendo vehículos por operación y ruta:",
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Obtiene todos los documentos requeridos para una operación específica
     */
    getDocumentsByOperation: async (operationId) => {
      try {
        const response = await api.get(
          `/vehicle/operations/${operationId}/documents`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error obteniendo documentos para operación ${operationId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Asigna un documento a una operación
     */
    assignDocumentToOperation: async (operationId, documentId) => {
      try {
        const response = await api.post(
          `/vehicle/operations/${operationId}/documents/${documentId}`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error asignando documento ${documentId} a operación ${operationId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Elimina la asignación de un documento a una operación
     */
    removeDocumentFromOperation: async (operationId, documentId) => {
      try {
        const response = await api.delete(
          `/vehicle/operations/${operationId}/documents/${documentId}`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error eliminando documento ${documentId} de operación ${operationId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Asigna múltiples documentos a una operación (reemplaza asignaciones existentes)
     */
    assignDocumentsToOperation: async (operationId, documentIds) => {
      try {
        const response = await api.post(
          `/vehicle/operations/${operationId}/documents`,
          { documentIds }
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error asignando documentos a operación ${operationId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Exportar programación de un día específico a Excel
     */
    exportDaySchedule: async (operationId, date, exportData) => {
      try {
        const response = await api.post(
          `/vehicle/operation/${operationId}/schedule/export`,
          {
            date,
            ...exportData,
          },
          {
            responseType: "blob", // Importante para recibir archivo binario
            headers: {
              "Content-Type": "application/json",
              Accept:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            },
          }
        );

        return response;
      } catch (error) {
        console.error("Error exportando programación:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Deshabilitacion temporal de un vehiculo
     */
    temporarilyDisableVehicle: async (relationId, disableData) => {
      try {
        const response = await api.put(
          `/vehicle/vehicle-operation/${relationId}/disable`,
          disableData
        );
        return response.data;
      } catch (error) {
        console.error("Error deshabilitando vehículo temporalmente:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Remover vehiculo de una ruta en una operacion
     */
    removeVehicleFromRoute: async (relationId, operationRouteId) => {
      try {
        const response = await api.put(
          `/vehicle/vehicle-operation/${relationId}/remove-from-route`,
          {
            operationRouteId,
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error eliminando vehículo de ruta:", error);
        throw error.response?.data || { message: error.message };
      }
    },
  };
};

/**
 * Servicio para la gestión de tipos de documentos en el sistema de documentación vehicular.
 * Proporciona métodos para realizar operaciones CRUD y consultas relacionadas con los tipos de documentos.
 * @returns {Object} Colección de métodos para interactuar con los endpoints de tipos de documentos
 */
export const useDocumentTypeService = () => {
  const api = useApi();

  return {
    /**
     * Obtiene todos los tipos de documentos con soporte para paginación, búsqueda y ordenamiento
     */
    getAllDocumentTypes: async (params = {}) => {
      try {
        const response = await api.get("/vehicle/document-vehicle-types", {
          params,
        });
        return response.data;
      } catch (error) {
        console.error("Error obteniendo tipos de documentos:", error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Obtiene un tipo de documento específico por su ID
     * @param {String} id Identificador UUID del tipo de documento
     * @returns {Promise<Object>} Promesa que resuelve a los datos del tipo de documento
     */
    getDocumentTypeById: async (id) => {
      try {
        const response = await api.get(`/vehicle/document-vehicle-types/${id}`);
        return response.data;
      } catch (error) {
        console.error(
          `Error obteniendo tipo de documento con ID ${id}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Crea un nuevo tipo de documento
     * @param {Object} data Datos del tipo de documento a crear
     * @param {String} data.name Nombre del tipo de documento
     * @param {String} [data.description] Descripción opcional del tipo de documento
     * @returns {Promise<Object>} Promesa que resuelve a los datos del nuevo tipo de documento
     */
    createDocumentType: async (data) => {
      try {
        const response = await api.post(
          "/vehicle/document-vehicle-types",
          data
        );
        return response.data;
      } catch (error) {
        console.error("Error creando tipo de documento:", error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Actualiza un tipo de documento existente
     * @param {String} id Identificador UUID del tipo de documento
     * @param {Object} data Datos actualizados del tipo de documento
     * @param {String} data.name Nombre actualizado del tipo de documento
     * @param {String} [data.description] Descripción actualizada del tipo de documento
     * @returns {Promise<Object>} Promesa que resuelve a los datos del tipo de documento actualizado
     */
    updateDocumentType: async (id, data) => {
      try {
        const response = await api.put(
          `/vehicle/document-vehicle-types/${id}`,
          data
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error actualizando tipo de documento con ID ${id}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Elimina un tipo de documento
     * @param {String} id Identificador UUID del tipo de documento
     * @returns {Promise<Object>} Promesa que resuelve al resultado de la eliminación
     */
    deleteDocumentType: async (id) => {
      try {
        const response = await api.delete(
          `/vehicle/document-vehicle-types/${id}`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error eliminando tipo de documento con ID ${id}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Obtiene todas las operaciones que requieren un documento específico
     * @param {String} documentId Identificador UUID del documento
     * @returns {Promise<Object>} Promesa que resuelve a las operaciones que requieren el documento
     */
    getOperationsByDocument: async (documentId) => {
      try {
        const response = await api.get(
          `/vehicle/document-vehicle-types/${documentId}/operations`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error obteniendo operaciones para documento ${documentId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Obtiene todos los tipos de vehículos que requieren un documento específico
     * @param {String} documentId Identificador UUID del documento
     * @returns {Promise<Object>} Promesa que resuelve a los tipos de vehículos que requieren el documento
     */
    getVehicleTypesByDocument: async (documentId) => {
      try {
        const response = await api.get(
          `/vehicle/vehicle/document-vehicle-types/${documentId}/vehicle-types`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error obteniendo tipos de vehículos para documento ${documentId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },
  };
};

/**
 * Servicio para la gestión de relaciones entre tipos de vehículos y documentos.
 * Proporciona métodos para asignar y gestionar los requisitos documentales por tipo de vehículo.
 * @returns {Object} Colección de métodos para interactuar con los endpoints de relaciones vehículo-documento
 */
export const useVehicleDocumentService = () => {
  const api = useApi();

  return {
    /**
     * Obtiene todos los documentos requeridos para un tipo de vehículo específico
     * @param {String} vehicleTypeId Identificador UUID del tipo de vehículo
     * @returns {Promise<Object>} Promesa que resuelve a los documentos requeridos
     */
    getDocumentsByVehicleType: async (vehicleTypeId) => {
      try {
        const response = await api.get(
          `/vehicle/vehicle-types/${vehicleTypeId}/documents`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error obteniendo documentos para tipo de vehículo ${vehicleTypeId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Asigna un documento a un tipo de vehículo
     * @param {String} vehicleTypeId Identificador UUID del tipo de vehículo
     * @param {String} documentId Identificador UUID del documento
     * @returns {Promise<Object>} Promesa que resuelve al resultado de la asignación
     */
    assignDocumentToVehicleType: async (vehicleTypeId, documentId) => {
      try {
        const response = await api.post(
          `/vehicle/vehicle-types/${vehicleTypeId}/documents/${documentId}`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error asignando documento ${documentId} a tipo de vehículo ${vehicleTypeId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Elimina la asignación de un documento a un tipo de vehículo
     * @param {String} vehicleTypeId Identificador UUID del tipo de vehículo
     * @param {String} documentId Identificador UUID del documento
     * @returns {Promise<Object>} Promesa que resuelve al resultado de la eliminación
     */
    removeDocumentFromVehicleType: async (vehicleTypeId, documentId) => {
      try {
        const response = await api.delete(
          `/vehicle/vehicle-types/${vehicleTypeId}/documents/${documentId}`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error eliminando documento ${documentId} de tipo de vehículo ${vehicleTypeId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Asigna múltiples documentos a un tipo de vehículo (reemplaza asignaciones existentes)
     * @param {String} vehicleTypeId Identificador UUID del tipo de vehículo
     * @param {Array<String>} documentIds Array de identificadores UUID de documentos
     * @returns {Promise<Object>} Promesa que resuelve al resultado de la asignación
     */
    assignDocumentsToVehicleType: async (vehicleTypeId, documentIds) => {
      try {
        const response = await api.post(
          `/vehicle/vehicle-types/${vehicleTypeId}/documents`,
          { documentIds }
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error asignando documentos a tipo de vehículo ${vehicleTypeId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },
  };
};

/**
 * Servicio para consultas complejas y agregadas en el sistema de documentación vehicular.
 * Proporciona métodos para realizar análisis, comparativas y verificaciones de cumplimiento documental.
 * @returns {Object} Colección de métodos para interactuar con los endpoints de consultas complejas
 */
export const useDocumentationAnalyticsService = () => {
  const api = useApi();

  return {
    /**
     * Obtiene requisitos documentales organizados por tipo de vehículo para una operación
     * @param {String} operationId Identificador UUID de la operación
     * @returns {Promise<Object>} Promesa que resuelve a los requisitos organizados por tipo de vehículo
     */
    getDocumentRequirementsByOperation: async (operationId) => {
      try {
        const response = await api.get(
          `/vehicle/documentation/operation-requirements/${operationId}`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error obteniendo requisitos para operación ${operationId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Obtiene un resumen global de requisitos documentales por documento
     * @returns {Promise<Object>} Promesa que resuelve al resumen de requisitos
     */
    getDocumentRequirementsSummary: async () => {
      try {
        const response = await api.get(
          "/vehicle/documentation/requirements-summary"
        );
        return response.data;
      } catch (error) {
        console.error(
          "Error obteniendo resumen de requisitos documentales:",
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Verifica documentos faltantes para un vehículo en una operación específica
     * @param {String} vehicleId Identificador UUID del vehículo
     * @param {String} operationId Identificador UUID de la operación
     * @returns {Promise<Object>} Promesa que resuelve a la lista de documentos faltantes
     */
    getMissingDocumentsForVehicle: async (vehicleId, operationId) => {
      try {
        const response = await api.get(
          `/vehicle/documentation/missing-documents/${vehicleId}/${operationId}`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error verificando documentos faltantes para vehículo ${vehicleId} en operación ${operationId}:`,
          error
        );
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Compara requisitos documentales entre múltiples operaciones
     * @param {Array<String>} operationIds Array de identificadores UUID de operaciones a comparar
     * @returns {Promise<Object>} Promesa que resuelve a la matriz comparativa
     */
    compareOperationRequirements: async (operationIds) => {
      try {
        // Convertir array a string separada por comas para parámetro de consulta
        const operationIdsParam = operationIds.join(",");
        const response = await api.get(
          `/vehicle/documentation/compare-operations`,
          {
            params: { operationIds: operationIdsParam },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error comparando requisitos entre operaciones:", error);
        throw error.response?.data || { message: error.message };
      }
    },

    /**
     * Obtiene estadísticas globales sobre documentación y cumplimiento
     * @returns {Promise<Object>} Promesa que resuelve a las estadísticas
     */
    getDocumentationStatistics: async () => {
      try {
        const response = await api.get("/vehicle/documentation/statistics");
        return response.data;
      } catch (error) {
        console.error("Error obteniendo estadísticas de documentación:", error);
        throw error.response?.data || { message: error.message };
      }
    },
  };
};
